import { useState } from 'react';
import { useT } from '@hana/i18n';
import { IconExternalLink, IconFileTypePdf, IconPlus, IconSend, IconSparkles, IconTrash } from '@tabler/icons-react';
import { GROUP_DOT } from './JobCards';
import { dockStats, dockSuggests } from './dockData';
import type { Job } from './types';
import './jobDockI18n';
import s from './jobDock.module.css';

// Bảng phải của Mint-Job-1920: Phân tích · Việc · Thư · Thông báo (cột cố định ≥ 1800). Chỉ đọc; nút ghi báo toast.
// Việc / Thư / Thông báo là dữ liệu mẫu của board.

export type DockTab = 'an' | 'task' | 'mail' | 'noti';

const TASKS: ReadonlyArray<{ k: string; tone: 'late' | 'today' | 'soon'; items: ReadonlyArray<[string, string, string, string]> }> = [
  { k: 'late', tone: 'late', items: [['TVA', 'Gán xe chuyến kéo cont', 'SE260639540', 'quá 2h'], ['VED', 'Khai VED trước CloseCY − 5h', 'SE260636900', 'quá 40 phút']] },
  { k: 'today', tone: 'today', items: [['PSI', 'Nộp SI cho hãng', 'SE260639370', '16:00'], ['FRT', 'Khoá cước & dịch vụ', 'SE260639540', '17:30'], ['FBL', 'Gửi Final B/L', 'SE260634310', '17:30']] },
  { k: 'soon', tone: 'soon', items: [['CLS', 'Đóng job đủ điều kiện', 'SE260598120', '24/Oct']] },
];

const NOTIS: ReadonlyArray<{ k: string; when: string; c: string; unread: boolean }> = [
  { k: 'YML báo delay YM CERTAINTY 089N · Closing CY 23/Oct 23:59', when: '10 phút trước', c: 'var(--d-amber)', unread: true },
  { k: 'GREENBUD đã xác nhận Draft B/L SE260636780', when: '32 phút trước', c: 'var(--d-green)', unread: true },
  { k: 'MKSF · BL Hold SE260637290 chờ khách chuyển tiền', when: '1 giờ trước', c: 'var(--err)', unread: false },
  { k: 'PhuongHT xác nhận booking ASC0549348 · SE260639540', when: '2 giờ trước', c: 'var(--d-blue)', unread: false },
  { k: 'COCOGROUP gửi RQ mới · 5×40RH VNSGN → CNNSA', when: 'Hôm qua 17:10', c: 'var(--d-violet)', unread: false },
];

interface Mail {
  id: string;
  box: string;
  from: string;
  when: string;
  subj: string;
  body: string;
  keys: string[];
  cus?: string;
  pod?: string;
}
export const DOCK_MAILS: readonly Mail[] = [
  { id: 'm1', box: 'info@', from: 'Yang Ming VN', when: '10:12', subj: 'Schedule change · YM CERTAINTY 089N · Closing CY 23/Oct 23:59', body: 'Please be advised vessel YM CERTAINTY 089N delayed. Booking YMJAI490606868 remains, closing CY 23/Oct 23:59.', keys: ['YMJAI490606868'] },
  { id: 'm2', box: 'info@', from: 'Maersk VN', when: '09:40', subj: 'Booking Confirmation 279024284 · MAERSK PORT KLANG 643N', body: 'Booking 279024284 confirmed 2x40HC VNSGN-CNNGB. SI cut-off 22/Oct 16:00.', keys: ['279024284'] },
  { id: 'm3', box: 'support@', from: 'GREENBUD · XNK', when: '09:12', subj: 'Re: Draft B/L A59GX35041 · xác nhận', body: 'Bên em xác nhận Draft B/L, nhờ HANA phát hành sớm giúp.', keys: ['A59GX35041'] },
  { id: 'm4', box: 'info@', from: 'COCOGROUP', when: 'Hôm qua 17:10', subj: 'New RQ 5x40RH VNSGN → CNNSA · ETD tuần 45', body: 'Dear HANA, pls book 5x40RH VNSGN-CNNSA, temp -18C, frozen coconut milk.', keys: [], cus: 'COCOGROUP', pod: 'CNNSA' },
];

export function matchMail(m: Mail, jobs: readonly Job[]): { by: 'key' | 'cus' | 'none'; jobs: Job[] } {
  const byK = jobs.filter((j) => m.keys.some((k) => j.bookingNo === k || j.extraRefs.includes(k)));
  if (byK.length) return { by: 'key', jobs: byK };
  const byC = m.cus ? jobs.filter((j) => j.customer === m.cus && j.pod === m.pod && j.status !== 'Cancelled') : [];
  return { by: byC.length ? 'cus' : 'none', jobs: byC };
}

export function JobDock({ rows, all, now, tab, onTab, onClose, onSuggest, onOpenJob, onNoWrite, onPickGroup }: {
  rows: readonly Job[];
  all: readonly Job[];
  now: string;
  tab: DockTab;
  onTab: (t: DockTab) => void;
  onClose: () => void;
  onSuggest: (ids: string[]) => void;
  onOpenJob: (id: string) => void;
  onNoWrite: () => void;
  onPickGroup: (group: string) => void;
}) {
  const t = useT();
  const [done, setDone] = useState<Record<string, true>>({});
  const [solved, setSolved] = useState<Record<string, true>>({});
  const [cur, setCur] = useState('m1');
  const [readAll, setReadAll] = useState(false);
  const [reply, setReply] = useState('');
  const mailN = DOCK_MAILS.filter((m) => !solved[m.id]).length;
  const notiN = readAll ? 0 : 2;
  const tabs: ReadonlyArray<{ key: DockTab; n: number; red?: boolean }> = [
    { key: 'an', n: 0 },
    { key: 'task', n: 5, red: true },
    { key: 'mail', n: mailN },
    { key: 'noti', n: notiN },
  ];

  const st = dockStats(rows, now);
  const sg = dockSuggests(rows, now);
  const svMax = Math.max(1, ...st.services.map((x) => x.n));
  const cusMax = Math.max(1, ...st.customers.map((x) => x.n));
  const saleMax = Math.max(1, ...st.sales.map((x) => x.n));
  const kpi: Array<[string, number, string]> = [
    [t('jobDock.kJobs'), st.total, ''],
    [t('jobDock.kCut'), st.cut48, st.cut48 ? 'var(--err)' : ''],
    [t('jobDock.kOver'), st.overdue, st.overdue ? 'var(--err)' : ''],
    [t('jobDock.kClose'), st.waitClose, ''],
  ];
  const mail = DOCK_MAILS.find((m) => m.id === cur) ?? DOCK_MAILS[0]!;
  const mm = matchMail(mail, all);
  const matchBy = mm.by === 'key' ? t('jobDock.byKey', { k: mail.keys.join(', ') }) : mm.by === 'cus' ? t('jobDock.byCus', { k: `${mail.cus} · ${mail.pod}` }) : t('jobDock.byNone');

  return (
    <aside className={s.dock}>
      <div className={s.head}>
        <div role="tablist" aria-label={t('jobDock.label')} className={s.tabs}>
          {tabs.map((x) => (
            <button key={x.key} type="button" role="tab" aria-selected={tab === x.key} className={`${s.tab} ${tab === x.key ? s.on : ''}`} onClick={() => onTab(x.key)}>
              {t(`jobDock.${x.key}`)}
              {x.n > 0 && <span className={s.n} style={{ background: x.red ? 'var(--err)' : 'var(--pri)' }}>{x.n}</span>}
            </button>
          ))}
        </div>
        <button type="button" className={s.close} aria-label={t('jobDock.close')} onClick={onClose}>
          ×
        </button>
      </div>

      {tab === 'an' && (
        <>
          {sg.length > 0 && (
            <section aria-label={t('jobDock.suggestLabel')} className={`${s.card} ${s.ask}`}>
              <h2 className={s.h}>
                <IconSparkles size={16} stroke={1.9} aria-hidden="true" />
                {t('jobDock.suggest')}
              </h2>
              {sg.map((g) => (
                <button key={g.key} type="button" className={s.sg} onClick={() => onSuggest(g.ids)}>
                  <div className={s.sgK}>{t(`jobDock.sg.${g.key}`, { n: g.ids.length })}</div>
                  <div className={s.sgS}>{t(`jobDock.sg.${g.key}Sub`)}</div>
                </button>
              ))}
            </section>
          )}
          <section className={s.card}>
            <div className={s.cardH}>
              <h2 className={s.h}>{t('jobDock.sum')}</h2>
              <span>{t('jobDock.scope', { n: st.total })}</span>
            </div>
            <div className={s.kpi}>
              {kpi.map(([k, v, c]) => (
                <div key={k}>
                  <div className={s.kK}>{k}</div>
                  <b className={s.kV} style={c ? { color: c } : undefined}>{v}</b> <span className={s.kK}>{t('jobDock.unit')}</span>
                </div>
              ))}
            </div>
          </section>
          <section className={s.card}>
            <h2 className={`${s.h} ${s.hb}`}>{t('jobDock.structure')}</h2>
            <div role="img" aria-label={`${t('jobDock.structure')} ${st.total}: ${st.groups.map((g) => `${g.group} ${g.n}`).join(', ')}`} className={s.seg}>
              {st.groups.map((g) => (
                <div key={g.group} style={{ flex: `${g.n} 1 0`, background: GROUP_DOT[g.group] }} />
              ))}
            </div>
            <div className={s.legend}>
              {st.groups.map((g) => (
                <button key={g.group} type="button" className={s.lg} onClick={() => onPickGroup(g.group)}>
                  <span className={s.dot} style={{ background: GROUP_DOT[g.group] }} />
                  <span className={s.lgK}>{g.group}</span>
                  <b>{g.n}</b>
                </button>
              ))}
            </div>
          </section>
          <section className={s.card}>
            <div className={`${s.cardH} ${s.hb}`}>
              <h2 className={s.h}>{t('jobDock.byService')}</h2>
              <span>{t('jobDock.byServiceU')}</span>
            </div>
            {st.services.map((v) => (
              <div key={v.k} className={s.svRow}>
                <span className={s.svK}>{v.k}</span>
                <div className={s.track}>
                  <div style={{ width: `${Math.round((v.n / svMax) * 100)}%` }} />
                </div>
                <b>{v.n}</b>
              </div>
            ))}
          </section>
          <section className={s.card}>
            <h2 className={`${s.h} ${s.hb}`}>{t('jobDock.topCus')}</h2>
            <div role="img" aria-label={`${t('jobDock.topCus')}: ${st.customers.map((c) => `${c.k} ${c.n}`).join(', ')}`}>
              {st.customers.map((c, i) => (
                <div key={c.k} className={s.cus}>
                  <div className={s.cusT}>
                    <span>
                      {i + 1}. {c.k}
                    </span>
                    <b>{c.n} job</b>
                  </div>
                  <div className={s.track}>
                    <div style={{ width: `${Math.max(2, Math.round((c.n / cusMax) * 100))}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className={s.card}>
            <h2 className={`${s.h} ${s.hb}`}>
              {t('jobDock.bySale')} <span className={s.hint}>{t('jobDock.bySaleHint')}</span>
            </h2>
            <div role="img" aria-label={`${t('jobDock.bySale')}: ${st.sales.map((x) => `${x.k} ${x.n} job, ${x.active}`).join('; ')}`}>
              {st.sales.map((x) => (
                <div key={x.k} className={s.saleRow}>
                  <span className={s.ab}>{x.k.slice(0, 2)}</span>
                  <div className={s.track}>
                    <div className={s.saleBar} style={{ width: `${Math.max(2, Math.round((x.n / saleMax) * 100))}%` }}>
                      <div style={{ flex: `${x.active} 1 0`, background: 'var(--d-blue)' }} />
                      <div style={{ flex: `${Math.max(0, x.n - x.active)} 1 0`, background: 'var(--pri-bd)' }} />
                    </div>
                  </div>
                  <b>{x.n} job</b>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {tab === 'task' &&
        TASKS.map((g) => (
          <div key={g.k}>
            <div className={s.tkH} style={{ color: g.tone === 'late' ? 'var(--err)' : g.tone === 'today' ? 'var(--tx)' : 'var(--tx2)' }}>
              <span>{t(`jobDock.${g.k}`)}</span>
              <span className={s.tkN}>{g.items.length}</span>
            </div>
            {g.items.map(([code, k, job, due]) => {
              const id = `${g.k}-${code}`;
              return (
                <label key={id} className={s.task} style={{ opacity: done[id] ? 0.55 : 1 }}>
                  <input type="checkbox" aria-label={t('jobDock.done', { k })} checked={!!done[id]} onChange={() => setDone((d) => { const n = { ...d }; if (n[id]) delete n[id]; else n[id] = true; return n; })} />
                  <div>
                    <div style={{ textDecoration: done[id] ? 'line-through' : 'none' }}>
                      <span className={s.code}>{code}</span>
                      {k}
                    </div>
                    <div className={s.due}>
                      <button type="button" className={s.jobLink} onClick={() => onOpenJob(job)}>{job}</button> · <span style={{ color: g.tone === 'late' ? 'var(--err)' : undefined }}>{due}</span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        ))}

      {tab === 'mail' && (
        <>
          <div className={s.due}>{t('jobDock.mailHint')}</div>
          {DOCK_MAILS.map((m) => {
            const x = matchMail(m, all);
            return (
              <button key={m.id} type="button" aria-pressed={m.id === cur} className={s.mailRow} style={{ background: m.id === cur ? 'var(--pri-bg)' : 'var(--sub)' }} onClick={() => { setCur(m.id); setReply(''); }}>
                <div className={s.mailTop}>
                  <b style={{ fontWeight: solved[m.id] ? 500 : 700 }}>{m.from}</b>
                  <span>{m.when}</span>
                </div>
                <div className={s.mailSubj}>{m.subj}</div>
                <div className={s.mailMatch} style={{ color: x.jobs.length ? 'var(--link)' : 'var(--warn)' }}>
                  {solved[m.id] ? t('jobDock.solvedPre') : ''}
                  {x.jobs.length ? t('jobDock.matchN', { n: x.jobs.length, no: x.jobs[0]!.id }) : t('jobDock.matchNone')}
                </div>
              </button>
            );
          })}
          <section aria-label={t('jobDock.viewing')} className={`${s.card} ${s.mailView}`}>
            <div>
              <b className={s.mvT}>{mail.subj}</b>
              <div className={s.due}>
                {mail.from} → {mail.box} · {mail.when}
              </div>
            </div>
            <div className={s.mvBody}>{mail.body}</div>
            <div className={s.match}>
              <div className={s.matchH}>{t('jobDock.matched', { by: matchBy })}</div>
              {mm.jobs.slice(0, 3).map((j) => (
                <div key={j.id} className={s.matchRow}>
                  <b>{j.id}</b>
                  <span className={s.matchSub}>{j.customer} · {j.status} · {j.vessel ?? '—'}</span>
                  <button type="button" className={s.mini} onClick={() => onOpenJob(j.id)}>
                    <IconExternalLink size={14} aria-hidden="true" />
                    {t('jobDock.open')}
                  </button>
                </div>
              ))}
              {mm.by === 'none' && (
                <>
                  <div className={s.warn}>{t('jobDock.noJob')}</div>
                  <button type="button" className={s.mini} onClick={onNoWrite}>
                    <IconPlus size={14} aria-hidden="true" />
                    {t('jobDock.newFromMail')} ↗
                  </button>
                </>
              )}
            </div>
            <textarea aria-label={t('jobDock.reply')} rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t('jobDock.replyPh', { box: mail.box })} className={s.ta} />
            <div className={s.acts}>
              <button type="button" className={s.primary} onClick={onNoWrite}>
                <IconSend size={16} aria-hidden="true" />
                {t('jobDock.send')}
              </button>
              <button type="button" className={`${s.sec} ${solved[mail.id] ? s.okBtn : ''}`} aria-pressed={!!solved[mail.id]} onClick={() => setSolved((d) => { const n = { ...d }; if (n[mail.id]) delete n[mail.id]; else n[mail.id] = true; return n; })}>
                {solved[mail.id] ? t('jobDock.solved') : t('jobDock.solve')}
              </button>
              <button type="button" className={s.sec} onClick={onNoWrite}>
                <IconFileTypePdf size={16} aria-hidden="true" />
                PDF
              </button>
              <button type="button" className={`${s.sec} ${s.errTx}`} onClick={onNoWrite}>
                <IconTrash size={16} aria-hidden="true" />
                {t('jobDock.del')}
              </button>
            </div>
          </section>
        </>
      )}

      {tab === 'noti' && (
        <>
          {NOTIS.map((n, i) => (
            <div key={n.k} className={s.noti} style={{ borderTopWidth: i ? 1 : 0 }}>
              <span className={s.nDot} style={{ background: n.c }} />
              <div>
                <div style={{ fontWeight: n.unread && !readAll ? 600 : 450 }}>{n.k}</div>
                <div className={s.due}>{n.when}</div>
              </div>
            </div>
          ))}
          <button type="button" className={s.readAll} onClick={() => setReadAll(true)}>
            {t('jobDock.readAll')}
          </button>
        </>
      )}
    </aside>
  );
}
